import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { adminLaunchEmailPreview, adminLaunchEmailSend } from "../lib/api";

/**
 * One-click "Email everyone on the waitlist" button.
 *
 * Lives next to the CSV download on the admin Launch-waitlist tab.
 * Fetches a server-side preview (how many would be sent, how many
 * already received the email, whether Resend is configured), shows
 * a confirm dialog, then fires the blast. Refreshes the parent table
 * via `onSent` so sent-at timestamps appear inline.
 */
const LaunchEmailBlastButton = ({ onSent }) => {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    try {
      const p = await adminLaunchEmailPreview();
      setPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load preview.");
    }
  };

  useEffect(() => {
    loadPreview();
  }, []);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await adminLaunchEmailSend();
      if (res?.ok) {
        const sent = res.sent || 0;
        const failed = res.failed || 0;
        if (sent > 0 && failed === 0) {
          toast.success(`Launch email sent to ${sent} teacher${sent === 1 ? "" : "s"}.`);
        } else if (sent > 0 && failed > 0) {
          toast.warning(`Sent ${sent}, failed ${failed}.`, {
            description: "Check the launch-email logs in the database for details.",
          });
        } else if (failed > 0) {
          toast.error(`All ${failed} sends failed — Resend may be rate-limited or misconfigured.`);
        } else {
          toast.info("No pending emails — everyone on the waitlist has already been emailed.");
        }
      } else {
        toast.error("Send failed.");
      }
      setOpen(false);
      await loadPreview();
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const pending = preview?.pending ?? 0;
  const alreadySent = preview?.already_sent ?? 0;
  const total = preview?.total_on_waitlist ?? 0;
  const configured = preview?.email_configured ?? false;
  const noPending = !configured ? false : pending === 0;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          data-testid="admin-launch-email-blast"
          disabled={!preview || !configured || noPending}
          className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white font-extrabold text-sm h-10 px-4 inline-flex items-center gap-2"
        >
          <Send className="h-3.5 w-3.5" />
          {!preview
            ? "Loading…"
            : !configured
            ? "Resend not configured"
            : noPending
            ? "All sent"
            : `Email ${pending} teacher${pending === 1 ? "" : "s"}`}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="admin-launch-email-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-[#1c2d3d]">
            <Send className="h-5 w-5" /> Send the launch email?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-[#4a6984]">
              <p>
                This will email <span className="font-extrabold text-[#1c2d3d]">{pending}</span>{" "}
                teacher{pending === 1 ? "" : "s"} on the launch waitlist using your Resend account.
              </p>
              {alreadySent > 0 && (
                <p className="text-sm">
                  <CheckCircle2 className="inline h-3.5 w-3.5 mr-1 text-emerald-600" />
                  {alreadySent} already received it — they won't be re-emailed.
                </p>
              )}
              <div className="rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7] p-3 text-xs space-y-1">
                <p>
                  <span className="font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d]/70">
                    Subject:
                  </span>{" "}
                  {preview?.subject}
                </p>
                <p>
                  <span className="font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d]/70">
                    Link:
                  </span>{" "}
                  <span className="underline">{preview?.pricing_url}</span>
                </p>
                {preview?.pending_preview?.length > 0 && (
                  <p>
                    <span className="font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d]/70">
                      First recipients:
                    </span>{" "}
                    {preview.pending_preview.join(", ")}
                    {pending > preview.pending_preview.length &&
                      ` … +${pending - preview.pending_preview.length} more`}
                  </p>
                )}
              </div>
              <p className="text-[11px] text-[#4a6984]/70 flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                This action can't be undone. Safe to re-click later — only
                un-emailed teachers will be targeted.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="admin-launch-email-cancel"
            disabled={sending}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="admin-launch-email-confirm"
            onClick={(e) => {
              e.preventDefault();
              handleSend();
            }}
            disabled={sending || pending === 0}
            className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white font-extrabold"
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <>Send to {pending} teacher{pending === 1 ? "" : "s"}</>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default LaunchEmailBlastButton;
